import { Alert, Button, ButtonProps, Stack, Typography } from '@mui/material';
import { GenericGeoEvent } from 'features/geoEvents/geoEvent.types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import * as Sentry from '@sentry/capacitor';
import { useNotificationsSettings } from 'features/notifications/hooks/useNotificationsSettings';
import { Mute } from '@capgo/capacitor-mute';
import { NativeAudio } from '@capacitor-community/native-audio';
import { getDenseResponsiveFontSize } from 'theme/watchdutyTheme.utils';
import { EnableNotificationsDialog } from './EnableNotificationsDialog';
import { useGeoEventNotifications } from './useGeoEventNotifications';

type GeoEventNotificationsButtonProps = {
  geoEvent: GenericGeoEvent;
};

type NotificationsEnabledButtonProps = GeoEventNotificationsButtonProps;

const NOTIFICATION_ASSET_ID = 'notification';

const getCommonButtonProps = (): ButtonProps => ({
  fullWidth: true,
  color: 'neutral',
  variant: 'outlined',
  size: 'large',
  sx: {
    fontSize: getDenseResponsiveFontSize('0.75rem'),
    textTransform: 'uppercase',
    fontWeight: 'fontWeightBold',
  },
});

const preloadNotificationPreviewSound = (): void => {
  // Enable mixed audio for notification sounds, allowing
  // them to play concurrently with other audio sessions.
  NativeAudio.configure({ focus: false });
  NativeAudio.preload({
    assetId: NOTIFICATION_ASSET_ID,
    assetPath: 'notification.wav',
    audioChannelNum: 1,
    isUrl: false,
  }).catch(() => {
    // Ignore "Error: Audio Asset already exists"
  });
};

const playNotificationPreviewSound = async (): Promise<void> => {
  try {
    const { value: isMuted } = await Mute.isMuted();
    if (isMuted) return; // Ignore if device is on silent mode
    await NativeAudio.play({ assetId: NOTIFICATION_ASSET_ID });
  } catch (err) {
    Sentry.captureException(err);
  }
};

const NotificationsDisabledButton = (): JSX.Element => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <>
      <Button
        {...getCommonButtonProps()}
        startIcon={<NotificationsActiveIcon sx={{ width: 16, height: 16 }} />}
        onClick={() => setDialogOpen(true)}
        data-testid="notifications-disabled-button"
      >
        {t('geoEvent.notifications.on')}
      </Button>

      <EnableNotificationsDialog open={dialogOpen} onSetOpen={setDialogOpen} />
    </>
  );
};

const NotificationsEnabledButton = (
  props: NotificationsEnabledButtonProps,
): JSX.Element => {
  const { geoEvent } = props;
  const { t } = useTranslation();
  const {
    isSubscribedToNotifications,
    showSubscribeToSilentNotifications,
    toggleNotificationsSubscription,
    subscribeToSilentNotifications,
  } = useGeoEventNotifications(geoEvent);

  useEffect(() => {
    preloadNotificationPreviewSound();
  }, []);

  const handleClick = async (): Promise<void> => {
    toggleNotificationsSubscription();
    if (!isSubscribedToNotifications) {
      await playNotificationPreviewSound();
    }
  };

  return (
    <Stack spacing={2}>
      <Button
        {...getCommonButtonProps()}
        startIcon={
          isSubscribedToNotifications ? (
            <NotificationsOffIcon sx={{ width: 16, height: 16 }} />
          ) : (
            <NotificationsActiveIcon sx={{ width: 16, height: 16 }} />
          )
        }
        onClick={handleClick}
        data-testid="notifications-enabled-button"
      >
        {isSubscribedToNotifications
          ? t('geoEvent.notifications.off')
          : t('geoEvent.notifications.on')}
      </Button>

      {showSubscribeToSilentNotifications && (
        <Alert
          severity="warning"
          icon={false}
          slotProps={{ message: { sx: { width: '100%' } } }}
        >
          <Stack spacing={1}>
            <Typography>
              {isSubscribedToNotifications
                ? t('geoEvent.notifications.silent.on')
                : t('geoEvent.notifications.silent.off')}
            </Typography>

            <Button
              onClick={subscribeToSilentNotifications}
              variant="watchduty-text-uppercase"
              sx={{ color: 'text.primary', width: 'fit-content' }}
            >
              {t('geoEvent.notifications.enableNotifications')}
            </Button>
          </Stack>
        </Alert>
      )}
    </Stack>
  );
};

export const GeoEventNotificationsButton = (
  props: GeoEventNotificationsButtonProps,
): JSX.Element => {
  const { geoEvent } = props;
  const { isPushEnabled } = useNotificationsSettings({
    regionSettingsQueryEnabled: false,
  });

  if (!isPushEnabled) {
    return <NotificationsDisabledButton />;
  }

  return <NotificationsEnabledButton geoEvent={geoEvent} />;
};
